<template>
  <fieldset>
    <legend class="text-h6 text-sm-h5">Employeur</legend>
    <!-- Information sur l'employeur -->
    <div v-if="!disabled">
      <!-- Employer id -->
      <v-autocomplete v-if="!noOrganisationFound" ref="organisme"
                      label="Nom de l'employeur"
                      :items="listeOrganisations"
                      item-text="label"
                      item-value="id"
                      prepend-icon="mdi-city"
                      v-model="organisation"
                      :search-input.sync="searchOrganisation"
                      return-object
                      @change="linkOrganisationToCarteDAffaire"
                      cache-items
                      required
                      :rules="rules.nomDeLEmployeur"
                      class="cssRequired">
        <template v-slot:no-data>
          <v-sheet class="pa-4">
            Vous ne trouvez pas votre employeur ?
            <v-btn class="mx-2" color="primary" @click="organisationNotFound">Créez le</v-btn>
          </v-sheet>
        </template>
      </v-autocomplete>
      <div v-if="noOrganisationFound">
        <v-text-field label="Nom de l'employeur"
                      v-model="organisation.label"
                      @change="linkOrganisationToCarteDAffaire"
                      required
                      :rules="rules.nomDeLEmployeur"
                      class="cssRequired"></v-text-field>
        <!-- Employer details -->
        <div class="ml-16">
          <!-- Adresse -->
          <CanadaPost :key="organisation.id"
                      :clef="getKey(indexCarteDAffaire)"
                      text="Adresse du siège social"
                      v-model="organisation.rue1"
                      @updateAdresse="updateOrganisationAddress"
                      required
                      cssClass="cssRequired"/>
          <!-- Ville -->
          <v-text-field label="Ville"
                        v-model="organisation.ville"
                        disabled
                        required
                        class="cssRequired"></v-text-field>
          <!-- Code Postal -->
          <v-text-field label="Code postal"
                        v-model="organisation.codePostal"
                        disabled
                        required
                        class="cssRequired"></v-text-field>

          <!-- Province -->
          <v-autocomplete :items="listeProvincesFilter"
                          :item-text="itemText"
                          :item-value="itemValue"
                          v-model="organisation.province"
                          label="Province"
                          return-object
                          disabled
                          required
                          class="cssRequired"></v-autocomplete>

          <!-- Pays -->
          <v-autocomplete :items="listePays"
                          :item-text="itemText"
                          :item-value="itemValue"
                          v-model="organisation.pays"
                          label="Pays"
                          return-object
                          disabled
                          required
                          class="cssRequired"></v-autocomplete>

          <v-row>
            <v-btn class="ma-4" color="primary" @click="validerLaSaisie">Valider ma saisie</v-btn>
            <v-btn class="ma-4" color="error" @click="annuler">Annuler</v-btn>
          </v-row>
        </div>
      </div>
    </div>
    <div v-if="isPratiqueAutonome">
      <address class="ml-8" style="color: rgba(0, 0, 0, 0.38)">
        <div>{{ organisation.label }}</div>
        <div>
          <span v-if="organisation.province">{{ organisation.province.lang00 }}, </span>
          <span v-if="organisation.pays">{{ organisation.pays.lang00 }}</span>
        </div>
      </address>
    </div>
    <div v-else-if="disabled || existingOrganisation">
      <address class="ml-8" style="color: rgba(0, 0, 0, 0.38)">
        <div>{{ organisation.label }}</div>
        <div>{{ organisation.rue1 }}</div>
        <div>
          <span v-if="organisation.ville">{{ organisation.ville }}, </span>
          <span v-if="organisation.province">{{ organisation.province.lang00 }}, </span>
          <span v-if="organisation.codePostal">{{ organisation.codePostal }}, </span>
          <span v-if="organisation.pays">{{ organisation.pays.lang00 }}</span>
        </div>
      </address>
    </div>
    <section class="legalSection body-1">
      <p class="mentionLegal">
        L'employeur est le nom de l'organisation qui vous rémunère, par exemple : CIUSSS de la Capitale-Nationale.
      </p>
      <p v-if="!isDroitsAcquis" class="font-italic">
        Si vous êtes en pratique autonome et que vous n'avez pas de lieu de travail,
        merci de saisir et de sélectionner PRATIQUE AUTONOME dans la liste Nom de l'employeur.
      </p>
    </section>
  </fieldset>
</template>

<script>
import { mapActions } from "vuex"
import CanadaPost from './canadaPost/CanadaPost'
import { pratiqueAutonomeId } from '@/plugins/constants'

export default {
  components: {
    CanadaPost: CanadaPost
  },
  props: {
    carteDAffaire: {
      type: Object,
      default: null
    },
    indexCarteDAffaire: {
      type: Number,
      default: 0
    },
    listePays: {
      type: Array,
      default: []
    },
    listeProvinces: {
      type: Array,
      default: []
    },
    isDroitsAcquis: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
  },
  data() {
    return {
      searchOrganisation: null,
      listeOrganisations: [],
      organisation: null,
      noOrganisationFound: false,
      rules: {
        nomDeLEmployeur: [(v) => (!!v && v.length != 0) || "Le nom de l'employeur est requis"],
        adresse: [(v) => !!v || "L'adresse est requise"],
        ville: [(v) => !!v || "La ville est requise"],
        province: [
          (v) => !this.isProvinceRequired || !!v && v.length != 0 || "La province ou l'état est requis"
        ],
        pays: [(v) => !!v && v.length != 0 || "Le pays est requis"],
        codePostal: [(v) => !!v || "Le code postal est requis"],
      },
      saisieLabel: null,
    }
  },
  async created() {
    if (this.carteDAffaire.pm) {
      if (Object.keys(this.carteDAffaire.organisation).length > 0) {
        this.organisation = this.carteDAffaire.organisation
      } else {
        const loadedOrganisation = await this.getOrganisation(this.carteDAffaire.pm)
        await this.queryOrganisation(loadedOrganisation.label)

        this.organisation = loadedOrganisation
        this.setOrganisationToCarteDAffaire({
          organisation: this.organisation,
          carteDAffaire: this.carteDAffaire
        })
        this.$emit('organisationChange', this.isSansPrecisions)
      }
      this.listeOrganisations.push(this.organisation)
    }
  },
  computed: {
    isProvinceRequired() {
      if (this.organisation.pays) {
        return (
            (this.organisation.pays.id === this.$catalogues.pays.canada ||
                this.organisation.pays.id === this.$catalogues.pays.france ||
                this.organisation.pays.id === this.$catalogues.pays.usa) &&
            this.organisation.province == null
        )
      } else {
        return false
      }
    },
    isPratiqueAutonome() {
      return this.organisation?.id === pratiqueAutonomeId
    },
    listeProvincesFilter() {
      return this.listeProvinces.filter(p => this.organisation.pays != null && p.parent == this.organisation.pays.id)
    },
    isSansPrecisions() {
      return this.organisation && this.organisation.statutEmployeur && this.organisation.statutEmployeur.id == process.env.VUE_APP_ORGANISME_STATUT_SANSPRECISIONS
    },
    existingOrganisation() {
      return this.organisation && this.organisation.id
    },
  },
  watch: {
    searchOrganisation(val) {
      val && this.queryOrganisation(val)
    }
  },
  methods: {
    ...mapActions({
      getOrganisationByLabel: "Organisation/getOrganisationByLabel",
      getOrganisation: "Organisation/getOrganisation",
      setOrganisationToCarteDAffaire: "CarteDAffaire/setOrganisationToCarteDAffaire",
      updateOrganisationFromCanadaPost: "CarteDAffaire/updateOrganisationFromCanadaPost",
    }),
    itemText: (item) => item.lang00,
    itemValue: (item) => item.id,
    async queryOrganisation(value) {
      this.listeOrganisations = await this.getOrganisationByLabel(value)
      this.saisieLabel = value
    },
    linkOrganisationToCarteDAffaire() {
      this.setOrganisationToCarteDAffaire({
        organisation: this.organisation,
        carteDAffaire: this.carteDAffaire
      })
      this.$emit('organisationChange', this.isSansPrecisions)
    },
    getKey(index) {
      return "o-" + index
    },
    updateOrganisationAddress(payload) {
      this.updateOrganisationFromCanadaPost({
        organisation: this.organisation,
        address: payload,
        listePays: this.listePays,
        listeProvinces: this.listeProvinces,
      })
      this.linkOrganisationToCarteDAffaire()
    },
    organisationNotFound() {
      this.noOrganisationFound = true
      this.organisation = {
        id: null,
        label: this.saisieLabel,
        rue1: null,
        ville: null,
        codePostal: null,
        province: null,
        pays: null,
        statutEmployeur: { id: process.env.VUE_APP_ORGANISME_STATUT_NONVALIDEE }
      }
    },
    annuler() {
      this.noOrganisationFound = false
      this.organisation = {}
      this.linkOrganisationToCarteDAffaire()
    },
    validerLaSaisie() {
      this.linkOrganisationToCarteDAffaire()
      this.$emit('validate-form')
    }
  }
}
</script>

<style>
</style>